/**
 * AdminEditUserModal — admin-only profile editor.
 *
 * Editable fields: first_name, last_name, phone, email, street, zip, city,
 * iban, account_holder. Backend (PATCH /api/users/{id}) enforces phone /
 * email uniqueness. When phone or email changes and `notify_user` is
 * checked, the backend mails the user about the change.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  X as XIcon, Save, Loader2, AlertTriangle, Phone as PhoneIcon,
  Mail as MailIcon,
} from "lucide-react";
import api from "@/lib/api";

const labelCls = "block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold";
const inputCls = "h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium";

export default function AdminEditUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [notify, setNotify] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      phone: user.phone || "",
      email: user.email || "",
      street: user.street || "",
      zip: user.zip || "",
      city: user.city || "",
      iban: user.iban || "",
      account_holder: user.account_holder || "",
    });
    setNotify(false);
  }, [user]);

  if (!user) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const phoneChanged = form.phone !== (user.phone || "");
  const emailChanged = form.email !== (user.email || "");
  const contactChanged = phoneChanged || emailChanged;

  const save = async () => {
    // Build patch with only changed fields
    const patch = {};
    Object.keys(form).forEach((k) => {
      const oldVal = user[k] || "";
      const newVal = form[k] || "";
      if (oldVal !== newVal) patch[k] = newVal || null;
    });
    if (Object.keys(patch).length === 0) {
      toast.info("Keine Änderungen");
      return;
    }
    // Confirm phone/email change
    if (contactChanged) {
      const msg = [];
      if (phoneChanged) msg.push(`Telefon: ${user.phone || "—"} → ${form.phone || "—"}`);
      if (emailChanged) msg.push(`E-Mail: ${user.email || "—"} → ${form.email || "—"}`);
      if (!window.confirm(
        "Achtung: Kontaktdaten ändern\n\n" + msg.join("\n") +
        "\n\nDer Benutzer loggt sich ab jetzt mit den neuen Daten ein. " +
        "Alte Sessions bleiben gültig bis zum nächsten Logout.\n\nFortfahren?",
      )) return;
    }
    if (notify && contactChanged) {
      patch.notify_user = true;
    }
    setSaving(true);
    try {
      const r = await api.patch(`/users/${user.id}`, patch);
      toast.success("Benutzer aktualisiert");
      onSaved?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-stretch sm:items-center justify-center sm:p-3"
      onClick={() => !saving && onClose?.()}
      data-testid="edit-user-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-xl max-h-[100vh] sm:max-h-[92vh] bg-[#18181B] border-t sm:border border-zinc-800 sm:rounded-2xl flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
          <h3 className="font-bold text-[15px]">
            Benutzer bearbeiten — {user.first_name} {user.last_name}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded" data-testid="edit-modal-close">
            <XIcon size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Name */}
          <Section title="Name">
            <Row>
              <Field label="Vorname">
                <input className={inputCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="edit-first-name" />
              </Field>
              <Field label="Nachname">
                <input className={inputCls} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="edit-last-name" />
              </Field>
            </Row>
          </Section>

          {/* Kontakt */}
          <Section title="Kontakt (Login-Identität)">
            <Row>
              <Field label="Telefon">
                <input
                  className={inputCls + (phoneChanged ? " border-amber-500/60" : "")}
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="+49…"
                  data-testid="edit-phone"
                />
              </Field>
              <Field label="E-Mail">
                <input
                  type="email"
                  className={inputCls + (emailChanged ? " border-amber-500/60" : "")}
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  data-testid="edit-email"
                />
              </Field>
            </Row>
            {contactChanged && (
              <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/30 flex gap-2 text-[11.5px] text-amber-200">
                <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                <div>
                  <strong>Achtung — Login-Identität ändern.</strong> Der Benutzer wird sich
                  ab jetzt mit den neuen {phoneChanged && "Telefon"}
                  {phoneChanged && emailChanged && "-/"}{emailChanged && "E-Mail"}-Daten
                  einloggen. Bestehende Sessions bleiben gültig.
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notify}
                      onChange={(e) => setNotify(e.target.checked)}
                      className="accent-amber-400"
                      data-testid="edit-notify-checkbox"
                    />
                    <span>Benutzer per E-Mail über die Änderung informieren</span>
                  </label>
                </div>
              </div>
            )}
          </Section>

          {/* Adresse */}
          <Section title="Adresse">
            <Row>
              <Field label="Straße + Nr." colSpan={2}>
                <input className={inputCls} value={form.street} onChange={(e) => set("street", e.target.value)} />
              </Field>
            </Row>
            <Row>
              <Field label="PLZ">
                <input className={inputCls} value={form.zip} onChange={(e) => set("zip", e.target.value)} />
              </Field>
              <Field label="Ort">
                <input className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} />
              </Field>
            </Row>
          </Section>

          {/* Bankdaten */}
          <Section title="Bankdaten">
            <Row>
              <Field label="IBAN">
                <input className={inputCls + " font-mono"} value={form.iban} onChange={(e) => set("iban", e.target.value)} />
              </Field>
              <Field label="Kontoinhaber">
                <input className={inputCls} value={form.account_holder} onChange={(e) => set("account_holder", e.target.value)} />
              </Field>
            </Row>
          </Section>
        </div>

        {/* Footer */}
        <div className="border-t border-zinc-800 px-5 py-3 flex justify-end gap-2 bg-[#0c0c0e]">
          <button
            onClick={onClose}
            className="h-10 px-3 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-[12px] font-medium"
          >
            Abbrechen
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="edit-modal-save-btn"
            className="h-10 px-4 rounded-lg bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold text-[12px] disabled:bg-zinc-800 disabled:text-zinc-300 inline-flex items-center gap-1.5"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}


function Section({ title, children }) {
  return (
    <section className="space-y-2.5">
      <h4 className="font-bold text-[12px] uppercase tracking-widest text-zinc-200">{title}</h4>
      {children}
    </section>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">{children}</div>;
}

function Field({ label, colSpan = 1, children }) {
  return (
    <div className={colSpan === 2 ? "sm:col-span-2" : ""}>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}
